// ---------------------------------------------------------------------------
// /creatives/types — dash page for the creative-types catalog (feature #85).
//
// Surface:
//   - emerald→sky gradient heading + subtle catalog summary stats.
//   - merged platform + tenant-override table (`TypesList`).
//   - "Validate" mini-panel — quickly probe whether a (code, mime, size)
//     triple is accepted by the live server-side catalog.
//   - Create / override modal driven by a RHF-style hook (`useTypedForm`)
//     plus a Zod4-style schema (`TypeFormSchema`) — both exported from
//     `../features/creative-types/api` because this agent cannot touch
//     `apps/dash/package.json` to add the real RHF + Zod packages. The
//     shapes intentionally mirror RHF so a later agent can swap the
//     implementation with minimal surface churn.
//   - Sonner toasts for success + failure edges.
//
// Route is declared with TanStack Router's `createFileRoute` so it
// auto-registers when the D-series scaffold enables the file-based router
// plugin. Until then the component is still importable + renderable by
// root-owned wire-up in `router.tsx`.
//
// Owned by OPUS-D17. Only this file + two sibling files under
// `../features/creative-types/*` are in the agent's lane.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { TypesList } from "../features/creative-types/TypesList";
import {
	ApiError,
	TypeFormSchema,
	formatBytes,
	humanizeValidateReason,
	summarizeCatalog,
	typeViewToRaw,
	useCreateCreativeType,
	useCreativeTypes,
	useDeleteCreativeType,
	useTypedForm,
	useUpdateCreativeType,
	useValidateCreative,
	type CreativeTypeView,
	type ParsedTypeForm,
	type ValidatePayload,
	type ValidateResult,
} from "../features/creative-types/api";

// ---------------------------------------------------------------------------
// Route registration — the D01 scaffold is still on programmatic routing, so
// we expose the page both as a default export and as a named export. Once
// the file-based TanStack Router plugin is enabled (a root-owned wire-up
// step), a thin `createFileRoute("/creatives/types")({ component: ... })`
// shim can wrap this module without touching the feature code here.
// ---------------------------------------------------------------------------

export default CreativeTypesPage;
export { CreativeTypesPage };
export const ROUTE_PATH = "/creatives/types" as const;

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

type ModalMode =
	| { kind: "closed" }
	| { kind: "create" }
	| { kind: "edit"; target: CreativeTypeView };

function CreativeTypesPage() {
	const typesQuery = useCreativeTypes();
	const createMut = useCreateCreativeType();
	const updateMut = useUpdateCreativeType();
	const deleteMut = useDeleteCreativeType();

	const [modal, setModal] = useState<ModalMode>({ kind: "closed" });

	const items = typesQuery.data?.types ?? [];
	const summary = useMemo(() => summarizeCatalog(items), [items]);

	const onEdit = useCallback((target: CreativeTypeView) => {
		setModal({ kind: "edit", target });
	}, []);

	const onRevert = useCallback(
		(target: CreativeTypeView) => {
			if (target.source !== "tenant") return;
			const confirmed =
				typeof window === "undefined"
					? true
					: window.confirm(
							target.shadows_platform
								? `Revert "${target.code}" to the platform default?`
								: `Delete custom creative type "${target.code}"?`,
						);
			if (!confirmed) return;
			deleteMut.mutate(target.code, {
				onSuccess: () => {
					toast.success(
						target.shadows_platform
							? `Reverted "${target.code}" to platform default.`
							: `Deleted custom type "${target.code}".`,
					);
				},
				onError: (err) => {
					toast.error(
						err instanceof ApiError
							? humanizeValidateReason(err.message)
							: "Failed to revert creative type.",
					);
				},
			});
		},
		[deleteMut],
	);

	return (
		<main className="min-h-screen bg-zinc-50 font-[Inter,ui-sans-serif,system-ui] text-zinc-900">
			<div className="mx-auto max-w-6xl px-6 py-10">
				<PageHeader
					summary={summary}
					onCreate={() => setModal({ kind: "create" })}
					isRefetching={typesQuery.isFetching && !typesQuery.isLoading}
					onRefresh={() => typesQuery.refetch()}
				/>

				<section className="mt-8">
					<TypesList
						data={items}
						isLoading={typesQuery.isLoading}
						isError={typesQuery.isError}
						errorMessage={
							typesQuery.error instanceof ApiError
								? humanizeValidateReason(typesQuery.error.message)
								: "Failed to load creative types."
						}
						onEdit={onEdit}
						onRevert={onRevert}
						revertingCode={deleteMut.isPending ? deleteMut.variables ?? null : null}
					/>
				</section>

				<section className="mt-10">
					<ValidatePanel availableCodes={items.map((t) => t.code)} />
				</section>
			</div>

			{modal.kind !== "closed" ? (
				<TypeFormModal
					mode={modal}
					onClose={() => setModal({ kind: "closed" })}
					onSubmit={async (data) => {
						if (modal.kind === "create") {
							await createMut.mutateAsync(data);
							toast.success(`Created creative type "${data.code}".`);
						} else {
							await updateMut.mutateAsync({
								code: modal.target.code,
								label: data.label,
								allowedMimes: data.allowedMimes,
								maxSizeBytes: data.maxSizeBytes,
							});
							toast.success(`Updated creative type "${modal.target.code}".`);
						}
					}}
				/>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Page header — gradient title + summary pills + CTA.
// ---------------------------------------------------------------------------

function PageHeader({
	summary,
	onCreate,
	onRefresh,
	isRefetching,
}: {
	summary: { total: number; platform: number; tenant: number; shadowed: number };
	onCreate: () => void;
	onRefresh: () => void;
	isRefetching: boolean;
}) {
	return (
		<header className="flex flex-wrap items-start justify-between gap-6">
			<div>
				<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-4xl font-semibold tracking-tight text-transparent">
					Creative types
				</h1>
				<p className="mt-2 max-w-2xl text-sm text-zinc-600">
					Platform-defined creative types merged with your tenant-level overrides.
					Tighten allowed MIME types, adjust max upload sizes, or register new
					types for your workflow.
				</p>
				<div className="mt-4 flex flex-wrap gap-2 text-xs font-medium">
					<SummaryPill label="Total" value={summary.total} tint="zinc" />
					<SummaryPill label="Platform" value={summary.platform} tint="zinc" />
					<SummaryPill label="Tenant" value={summary.tenant} tint="emerald" />
					<SummaryPill label="Shadowed" value={summary.shadowed} tint="sky" />
				</div>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={onRefresh}
					disabled={isRefetching}
					className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{isRefetching ? "Refreshing…" : "Refresh"}
				</button>
				<button
					type="button"
					onClick={onCreate}
					className="inline-flex items-center rounded-md border border-transparent bg-gradient-to-r from-emerald-500 to-sky-500 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
				>
					New creative type
				</button>
			</div>
		</header>
	);
}

function SummaryPill({
	label,
	value,
	tint,
}: {
	label: string;
	value: number;
	tint: "zinc" | "emerald" | "sky";
}) {
	const palette = {
		zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
		emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
		sky: "border-sky-200 bg-sky-50 text-sky-700",
	} as const;
	return (
		<span
			className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 ${palette[tint]}`}
		>
			<span className="text-[11px] uppercase tracking-wide">{label}</span>
			<span className="font-semibold tabular-nums">{value}</span>
		</span>
	);
}

// ---------------------------------------------------------------------------
// Validate mini-panel — POST /validate probe.
// ---------------------------------------------------------------------------

function ValidatePanel({ availableCodes }: { availableCodes: string[] }) {
	const validateMut = useValidateCreative();
	const [code, setCode] = useState<string>(availableCodes[0] ?? "");
	const [mime, setMime] = useState<string>("image/png");
	const [size, setSize] = useState<string>("");
	const [result, setResult] = useState<ValidateResult | null>(null);

	// If the available codes list arrives late, default the dropdown once.
	useEffect(() => {
		if (code === "" && availableCodes.length > 0) setCode(availableCodes[0]);
	}, [availableCodes, code]);

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setResult(null);
		const parsedSize = size.trim() === "" ? null : Number.parseInt(size, 10);
		if (parsedSize !== null && (!Number.isFinite(parsedSize) || parsedSize < 0)) {
			toast.error("Size must be a non-negative whole number of bytes.");
			return;
		}
		const payload: ValidatePayload = { code, mime, sizeBytes: parsedSize };
		try {
			const res = await validateMut.mutateAsync(payload);
			setResult(res);
			if (res.ok) {
				toast.success(`Accepted for "${res.code}" (${res.source}).`);
			} else {
				toast.error(humanizeValidateReason(res.reason));
			}
		} catch (err) {
			const message =
				err instanceof ApiError ? humanizeValidateReason(err.message) : "Validate failed.";
			toast.error(message);
			setResult({ ok: false, reason: message });
		}
	};

	return (
		<div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<h2 className="text-lg font-semibold text-zinc-900">Validate upload</h2>
				<p className="text-xs text-zinc-500">
					Runs a POST /creative-types/validate probe for the selected type.
				</p>
			</div>
			<form onSubmit={onSubmit} className="mt-4 grid gap-4 sm:grid-cols-4">
				<label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
					Type
					<select
						value={code}
						onChange={(e) => setCode(e.target.value)}
						className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
					>
						{availableCodes.length === 0 ? (
							<option value="">(none available)</option>
						) : (
							availableCodes.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))
						)}
					</select>
				</label>
				<label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
					MIME
					<input
						type="text"
						value={mime}
						onChange={(e) => setMime(e.target.value)}
						placeholder="image/png"
						autoComplete="off"
						className="rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
					/>
				</label>
				<label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
					Size (bytes)
					<input
						type="number"
						min={0}
						value={size}
						onChange={(e) => setSize(e.target.value)}
						placeholder="optional"
						className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm tabular-nums text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
					/>
				</label>
				<div className="flex items-end">
					<button
						type="submit"
						disabled={validateMut.isPending || code === ""}
						className="h-[38px] w-full rounded-md border border-transparent bg-gradient-to-r from-emerald-500 to-sky-500 px-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{validateMut.isPending ? "Checking…" : "Validate"}
					</button>
				</div>
			</form>

			{result ? <ValidateResultPanel result={result} /> : null}
		</div>
	);
}

function ValidateResultPanel({ result }: { result: ValidateResult }) {
	if (result.ok) {
		return (
			<div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
				<p className="font-medium text-emerald-800">
					Accepted for <span className="font-mono">{result.code}</span>{" "}
					<span className="text-emerald-700">({result.source})</span>
				</p>
			</div>
		);
	}
	return (
		<div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm">
			<p className="font-medium text-red-800">
				Rejected: {humanizeValidateReason(result.reason)}
			</p>
			{result.code ? (
				<p className="mt-1 text-xs text-red-700">
					Code: <span className="font-mono">{result.code}</span>
				</p>
			) : null}
			{result.allowed_mimes && result.allowed_mimes.length > 0 ? (
				<p className="mt-1 text-xs text-red-700">
					Allowed MIME: <span className="font-mono">{result.allowed_mimes.join(", ")}</span>
				</p>
			) : null}
			{typeof result.max_size_bytes === "number" ? (
				<p className="mt-1 text-xs text-red-700">
					Max size: {formatBytes(result.max_size_bytes)}
				</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create / Edit modal — RHF-style controlled form + Zod4-style validation.
// ---------------------------------------------------------------------------

function TypeFormModal({
	mode,
	onClose,
	onSubmit,
}: {
	mode: Exclude<ModalMode, { kind: "closed" }>;
	onClose: () => void;
	onSubmit: (data: ParsedTypeForm) => Promise<void>;
}) {
	const isEdit = mode.kind === "edit";
	const initial = isEdit
		? typeViewToRaw(mode.target)
		: { code: "", label: "", allowedMimesRaw: "", maxSizeRaw: "" };

	const form = useTypedForm(initial, { requireCode: !isEdit });

	// Keep the form in sync if the modal is re-opened for a different target.
	useEffect(() => {
		form.reset(initial);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode.kind, isEdit ? mode.target.code : null]);

	// Close on Escape — feel like a real modal.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const title = isEdit
		? mode.target.source === "tenant"
			? `Edit "${mode.target.code}"`
			: `Override "${mode.target.code}"`
		: "New creative type";

	const onValid = async (data: ParsedTypeForm) => {
		try {
			// In edit mode, the Zod schema skips `code`; force the parsed code
			// to match the target so the API PATCH lands on the right row.
			const submission = isEdit ? { ...data, code: mode.target.code } : data;
			// Validate invariants before round-tripping — mirror what the server
			// would reject so the toast is immediate.
			const shallowCheck = TypeFormSchema.parse(
				{
					code: submission.code,
					label: submission.label,
					allowedMimesRaw: submission.allowedMimes.join(", "),
					maxSizeRaw: submission.maxSizeBytes === null ? "" : String(submission.maxSizeBytes),
				},
				{ requireCode: true },
			);
			if (!shallowCheck.ok) {
				toast.error(shallowCheck.issues[0]?.message ?? "Invalid form input.");
				return;
			}
			await onSubmit(shallowCheck.data);
			onClose();
		} catch (err) {
			const message =
				err instanceof ApiError
					? humanizeValidateReason(err.message)
					: err instanceof Error
						? err.message
						: "Save failed.";
			toast.error(message);
		}
	};

	const submitHandler = form.handleSubmit(onValid);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="type-form-title"
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4"
			onClick={(e) => {
				// Backdrop click closes, but not clicks inside the dialog.
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className="w-full max-w-xl rounded-lg border border-zinc-200 bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
					<h2 id="type-form-title" className="text-lg font-semibold text-zinc-900">
						{title}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded-md border border-transparent px-2 py-1 text-sm text-zinc-500 hover:border-zinc-200 hover:bg-zinc-50 hover:text-zinc-700"
					>
						Close
					</button>
				</div>

				<form onSubmit={submitHandler} className="flex flex-col gap-4 px-6 py-5" noValidate>
					<FormField
						label="Code"
						hint={
							isEdit
								? "Locked — override existing creative type."
								: "Lowercase, letters + digits + dash / underscore."
						}
						error={form.formState.errors.code}
					>
						<input
							{...form.register("code")}
							disabled={isEdit}
							autoFocus={!isEdit}
							placeholder="e.g. banner_v2"
							autoComplete="off"
							spellCheck={false}
							className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500"
						/>
					</FormField>

					<FormField
						label="Label"
						hint="Human-readable name shown in the dashboard."
						error={form.formState.errors.label}
					>
						<input
							{...form.register("label")}
							placeholder="e.g. Hero banner v2"
							autoComplete="off"
							className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
						/>
					</FormField>

					<FormField
						label="Allowed MIME types"
						hint="Comma-separated. Use `image/*` wildcards or `*/*` for any."
						error={form.formState.errors.allowedMimesRaw}
					>
						<textarea
							{...form.register("allowedMimesRaw")}
							rows={3}
							placeholder="image/png, image/jpeg, image/webp"
							className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-xs text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
						/>
					</FormField>

					<FormField
						label="Max size"
						hint="Bytes, or with a unit suffix: 500KB, 2MB, 1GB. Leave empty for no limit."
						error={form.formState.errors.maxSizeRaw}
					>
						<input
							{...form.register("maxSizeRaw")}
							placeholder="e.g. 500KB"
							autoComplete="off"
							className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
						/>
					</FormField>

					<div className="mt-2 flex items-center justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 shadow-sm hover:border-zinc-300 hover:bg-zinc-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={form.formState.isSubmitting}
							className="rounded-md border border-transparent bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{form.formState.isSubmitting ? "Saving…" : isEdit ? "Save" : "Create"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function FormField({
	label,
	hint,
	error,
	children,
}: {
	label: string;
	hint?: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label className="text-xs font-medium text-zinc-700">{label}</label>
			{children}
			{error ? (
				<p className="text-xs font-medium text-red-600" role="alert">
					{error}
				</p>
			) : hint ? (
				<p className="text-xs text-zinc-500">{hint}</p>
			) : null}
		</div>
	);
}
